import type { BoundingBox } from 'js/src/client';

import type { ClipShot } from './clipFraming';

/**
 * The latest shot table per scene group, so framing tools can tell when a scene spans a change of
 * camera angle. Keyed by scene group and checked against its recording, since the analysis is
 * only good for the recording it was made from.
 */
export interface StoredClipFraming {
    recordingUuid: string;
    angleChangeTimesMs: number[];
    shots: Pick<ClipShot, 'number' | 'startMs' | 'endMs' | 'angle' | 'isBroll'>[];
}

/** A speaker crop framed on one face of one camera angle, for a slot of one size. */
export interface StoredSpeakerCrop {
    sceneShortId: string;
    scale: number;
    xTranslation: number;
    yTranslation: number;
    face: BoundingBox;
    faces: BoundingBox[];
}

const framingBySceneGroup = new Map<string, StoredClipFraming>();
const speakerCropsBySceneGroup = new Map<string, Map<string, StoredSpeakerCrop>>();

export function rememberClipFraming(sceneGroupId: string, framing: StoredClipFraming): void {
    framingBySceneGroup.set(sceneGroupId, framing);
    // A new analysis may letter the angles differently, so crops kept under the old letters go.
    speakerCropsBySceneGroup.delete(sceneGroupId);
}

function speakerCropKey(angle: string, face: number, slot: { width: number; height: number }) {
    return `${angle}|${face}|${Math.round(slot.width)}x${Math.round(slot.height)}`;
}

export function rememberSpeakerCrop(
    sceneGroupId: string,
    angle: string,
    face: number,
    slot: { width: number; height: number },
    crop: StoredSpeakerCrop,
): void {
    const crops = speakerCropsBySceneGroup.get(sceneGroupId) ?? new Map();
    crops.set(speakerCropKey(angle, face, slot), crop);
    speakerCropsBySceneGroup.set(sceneGroupId, crops);
}

export function getSpeakerCrop(
    sceneGroupId: string,
    angle: string,
    face: number,
    slot: { width: number; height: number },
): StoredSpeakerCrop | null {
    return (
        speakerCropsBySceneGroup.get(sceneGroupId)?.get(speakerCropKey(angle, face, slot)) ?? null
    );
}

export function getClipFraming(
    sceneGroupId: string,
    recordingUuid: string,
): StoredClipFraming | null {
    const framing = framingBySceneGroup.get(sceneGroupId);
    return framing && framing.recordingUuid === recordingUuid ? framing : null;
}

export function clearClipFraming(): void {
    framingBySceneGroup.clear();
    speakerCropsBySceneGroup.clear();
}
